import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@client/components/ui/dialog';
import { Button } from '@client/components/ui/button';
import { useIsPilotRestricted } from '@/shared/hooks/use-active-pilot-program';
import { AdvisorHierarchyConsentDialog } from './AdvisorHierarchyConsentDialog';

interface ConsentData {
  hasConsented: boolean;
  ipAddress: string | null;
  userAgent: string;
}

interface AcceptInvitationDialogProps {
  open: boolean;
  invitation: PendingSupervisorInvitation | null;
  onAccept: (consentData: ConsentData) => void;
  onDecline: () => void;
  isSubmitting?: boolean;
}

export function AcceptInvitationDialog({
  open,
  invitation,
  onAccept,
  onDecline,
  isSubmitting = false,
}: AcceptInvitationDialogProps) {
  const isClientSharingRestricted = useIsPilotRestricted('client-sharing');

  if (!invitation) {
    return null;
  }

  // An invitation sent before the pilot withheld client sharing can still ask for client access.
  // The server refuses to accept it, so say why here instead of letting the request fail.
  const exposesClients = isClientSharingRestricted && invitation.accessMode !== 'none';

  const supervisorName =
    invitation.supervisorFirstName && invitation.supervisorLastName
      ? `${invitation.supervisorFirstName} ${invitation.supervisorLastName}`
      : invitation.supervisorEmail;

  const supervisorOrgName =
    invitation.supervisorOrganizationDisplayName || invitation.supervisorOrganizationName;

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen && !isSubmitting) {
          onDecline();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader className="sr-only">
          <DialogTitle>Accept Invitation</DialogTitle>
          <DialogDescription>
            Review and accept the invitation to join this organization
          </DialogDescription>
        </DialogHeader>
        {exposesClients ? (
          <div className="space-y-4">
            <h3 className="text-2xl font-semibold text-gray-900">
              This invitation can't be accepted
            </h3>
            <p className="text-gray-700">
              Sharing clients with another advisor is not part of the pilot, and this invitation
              asks for access to your clients. Ask {supervisorName} to send a new invitation
              without client access.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={onDecline}
            >
              Close
            </Button>
          </div>
        ) : (
          <AdvisorHierarchyConsentDialog
            supervisorName={supervisorName}
            supervisorOrgName={supervisorOrgName}
            accessMode={invitation.accessMode}
            onAccept={onAccept}
            onDecline={onDecline}
            isSubmitting={isSubmitting}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
